<template>
  <NoticeBase
    :notice="notice"
    :relative-time="relativeTime"
    notice-class="inbox-automation-ask-user-notice"
    @open="open"
    @archived="$emit('archived', $event)"
  >
    <span class="issuer">
      <AutomationIcon class="automation-icon" />
      <span class="issuer-name">{{ notice.automation_name }}</span>
    </span>
    <span class="text">is waiting for your input</span>
  </NoticeBase>
</template>

<script lang="ts">
import { defineComponent, PropType, toRef } from "vue";

import AutomationIcon from "@/components/icons/AutomationIcon.vue";
import {
  openNoticeLocation,
  useNoticeRelativeTime,
  type AutomationAskUserNotice,
} from "@/components/inbox/notice";
import NoticeBase from "@/components/inbox/NoticeBase.vue";
import * as inject from "@/components/inject";
import { RouteNames } from "@/routes/types";

export default defineComponent({
  name: "InboxAutomationAskUserNotice",
  components: { NoticeBase, AutomationIcon },
  props: {
    notice: {
      type: Object as PropType<AutomationAskUserNotice>,
      required: true,
    },
  },
  emits: {
    archived: (_noticeId: string) => true,
  },
  setup(props) {
    const controller = inject.controller();
    const logger = inject.logger("InboxAutomationAskUserNotice");
    const router = inject.router();
    const automations = inject.automations();

    const noticeRef = toRef(props, "notice");

    const relativeTime = useNoticeRelativeTime(noticeRef);

    const open = async () => {
      if (!props.notice.viewed) {
        try {
          await controller.notice.markViewed(props.notice);
        } catch (e) {
          logger.error("Unable to mark notice as viewed", e);
        }
      }
      // The notice does not carry the organization that owns the run, so
      // probe the organizations tracked by the app-wide automations store
      // (every organization with agents) until one resolves the run.
      const automationsByOrg = automations.value?.all.value ?? {};
      for (const orgId of Object.keys(automationsByOrg)) {
        let automationId: string | undefined;
        try {
          const runRef = await controller.ai.automation.run.get(
            orgId,
            props.notice.run_id
          );
          automationId = runRef.value?.automation_id;
          runRef.release?.();
        } catch {
          continue;
        }
        if (!automationId) continue;
        router.push({
          name: RouteNames.Automation,
          params: { objectId: automationId },
          query: { organizationId: orgId, runId: props.notice.run_id },
        });
        return;
      }
      logger.error(
        "Unable to resolve the organization of automation run",
        props.notice.run_id
      );
      // The automation (or its run) may have been deleted. Open the notice
      // location in-app so a leftover `object:…` id shows Not found instead
      // of being handed to the OS.
      if (props.notice.location) {
        await openNoticeLocation(
          controller,
          router,
          logger.base(),
          props.notice.location
        );
      }
    };

    return {
      open,
      relativeTime,
    };
  },
});
</script>

<style scoped lang="scss">
.issuer {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  vertical-align: bottom;
}

.automation-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.automation-icon :deep(svg) {
  width: 18px;
  height: 18px;
}

.issuer-name {
  font-weight: 500;
}

.text {
  margin-inline-start: 6px;
  @include themed() {
    color: t($grey-60);
  }
}
</style>
